import { type ComponentProps, useCallback, useEffect, useRef, useState } from 'react';

import Button from '@/design_system/Button';
import InputText from '@/design_system/InputText';
import Stack from '@/design_system/Stack';
import IconCheck from '@/icons/Check.svg';
import IconCross from '@/icons/Cross.svg';
import IconEdit from '@/icons/Edit.svg';

import Title from '../Title';

type TitleOwnProps = ComponentProps<typeof Title>;

type EditableTitleProps = {
  value: string;
  onSave: (next: string) => void | Promise<void>;
  isSaving?: boolean;
  canEdit?: boolean;
  editAriaLabel: string;
  saveAriaLabel: string;
  cancelAriaLabel: string;
  inputAriaLabel: string;
  titleProps?: Omit<TitleOwnProps, 'children'>;
  className?: string;
};

const EditableTitleEditForm = ({
  initialValue,
  onSave,
  onCancel,
  isSaving,
  saveAriaLabel,
  cancelAriaLabel,
  inputAriaLabel,
}: {
  initialValue: string;
  onSave: (next: string) => void | Promise<void>;
  onCancel: () => void;
  isSaving: boolean;
  saveAriaLabel: string;
  cancelAriaLabel: string;
  inputAriaLabel: string;
}) => {
  const [draft, setDraft] = useState(initialValue);

  useEffect(() => {
    setDraft(initialValue);
  }, [initialValue]);

  const trimmed = draft.trim();
  const canSave = trimmed.length > 0 && trimmed !== initialValue;
  const submitInFlightRef = useRef(false);

  const handleSave = useCallback(async () => {
    if (!canSave || isSaving || submitInFlightRef.current) {
      return;
    }

    submitInFlightRef.current = true;

    try {
      await onSave(trimmed);
    } finally {
      submitInFlightRef.current = false;
    }
  }, [canSave, isSaving, onSave, trimmed]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onCancel();
    }

    if (e.key === 'Enter') {
      e.preventDefault();
      void handleSave();
    }
  };

  return (
    <Stack
      row
      gap="0.5rem"
      alignItems="center"
      flexWrap="nowrap"
      style={{ minWidth: 0, flex: '1' }}
    >
      <InputText
        size="medium"
        value={draft}
        onChange={setDraft}
        aria-label={inputAriaLabel}
        isLoading={isSaving}
        onKeyDown={onKeyDown}
        style={{ flex: 1, minWidth: 0 }}
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus
      />
      <Button
        ariaLabel={saveAriaLabel}
        tooltip={saveAriaLabel}
        size="large"
        variant="primary"
        onPress={() => void handleSave()}
        isLoading={isSaving}
        disabled={!canSave || isSaving}
        iconOnly
      >
        <IconCheck />
      </Button>
      <Button
        ariaLabel={cancelAriaLabel}
        tooltip={cancelAriaLabel}
        size="large"
        variant="secondary"
        onPress={onCancel}
        disabled={isSaving}
        iconOnly
      >
        <IconCross />
      </Button>
    </Stack>
  );
};

const EditableTitle = ({
  value,
  onSave,
  isSaving = false,
  canEdit = true,
  editAriaLabel,
  saveAriaLabel,
  cancelAriaLabel,
  inputAriaLabel,
  titleProps = {},
  className,
}: EditableTitleProps) => {
  const [isEditing, setIsEditing] = useState(false);

  const handleSave = useCallback(
    async (next: string) => {
      await onSave(next);
      setIsEditing(false);
    },
    [onSave]
  );

  if (isEditing) {
    return (
      <EditableTitleEditForm
        key={value}
        initialValue={value}
        onSave={handleSave}
        onCancel={() => setIsEditing(false)}
        isSaving={isSaving}
        saveAriaLabel={saveAriaLabel}
        cancelAriaLabel={cancelAriaLabel}
        inputAriaLabel={inputAriaLabel}
      />
    );
  }

  return (
    <Stack
      row
      gap="0.5rem"
      alignItems="center"
      flexWrap="nowrap"
      style={{ minWidth: 0, flex: '1' }}
      className={className}
    >
      <Title {...titleProps} style={{ flex: 1, minWidth: 0, ...titleProps?.style }}>
        {value}
      </Title>
      {canEdit && (
        <Button
          ariaLabel={editAriaLabel}
          tooltip={editAriaLabel}
          size="medium"
          variant="secondary"
          iconOnly
          onPress={() => setIsEditing(true)}
        >
          <IconEdit />
        </Button>
      )}
    </Stack>
  );
};

export default EditableTitle;
